import React from 'react';
import { JDProcessingStatus, ProcessingStage } from '../../lib/types/api';

interface JDProcessingProgressProps {
  status: JDProcessingStatus;
  className?: string;
}

const stageLabels: Record<ProcessingStage, string> = {
  uploading: 'Uploading File',
  extracting: 'Extracting Text',
  parsing: 'Parsing with AI',
  validating: 'Validating Data',
  completed: 'Completed'
};

const stageIcons: Record<ProcessingStage, string> = {
  uploading: '📁',
  extracting: '📝',
  parsing: '🤖',
  validating: '✅',
  completed: '🎉'
};



const getStageOrder = (stage: ProcessingStage): number => {
  const order: Record<ProcessingStage, number> = {
    uploading: 1,
    extracting: 2,
    parsing: 3,
    validating: 4,
    completed: 5
  };
  return order[stage];
};

export const JDProcessingProgress: React.FC<JDProcessingProgressProps> = ({ 
  status, 
  className = '' 
}) => {
  const stages: ProcessingStage[] = ['uploading', 'extracting', 'parsing', 'validating', 'completed'];

  return (
    <div className={`bg-gray-50 dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="text-left">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Processing Job Description
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {status.original_filename} • {status.file_size} bytes
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-primary-500">
            {status.progress}%
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {status.status === 'completed' ? 'Complete' : 'In Progress'}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
          <div 
            className="bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-500 h-3 rounded-full transition-all duration-500 ease-out shadow-glow-primary"
            style={{ width: `${status.progress}%` }}
          />
        </div>
      </div>

      {/* Stage Indicators */}
      <div className="grid grid-cols-5 gap-2 mb-6">
        {stages.map((stage) => {
          const isActive = stage === status.stage;
          const isCompleted = getStageOrder(stage) < getStageOrder(status.stage);
          
          return (
            <div
              key={stage}
              className={`flex flex-col items-center p-3 rounded-lg transition-all duration-300 ${
                isActive 
                  ? 'bg-primary-100 dark:bg-primary-900/40 border-2 border-primary-500/50 shadow-glow-primary' 
                  : isCompleted 
                    ? 'bg-green-100 dark:bg-green-900/40 border border-green-500/40'
                    : 'bg-gray-100 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600'
              }`}
            >
              <div className={`text-2xl mb-1 transition-all duration-300 ${
                isActive ? 'text-primary-500' : isCompleted ? 'text-green-500' : 'text-gray-400 dark:text-gray-500'
              }`}>
                {stageIcons[stage]}
              </div>
              <div className={`text-xs font-medium text-center transition-all duration-300 ${
                isActive ? 'text-gray-900 dark:text-white' : isCompleted ? 'text-green-700 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'
              }`}>
                {stageLabels[stage]}
              </div>
              {isActive && (
                <div className="mt-1">
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse shadow-glow-primary" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Status Message */}
      <div className="text-center">
        <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">
          {status.message}
        </div>
        {status.estimated_completion && status.status !== 'completed' && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Estimated completion: {status.estimated_completion}
          </div>
        )}
      </div>

      {/* Error Display */}
      {status.status === 'failed' && status.error_message && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg">
          <div className="flex items-center">
            <div className="text-red-500 mr-2">❌</div>
            <div className="text-sm text-red-700 dark:text-red-300">
              <strong>Processing Failed:</strong> {status.error_message}
            </div>
          </div>
          {status.error_details && (
            <div className="mt-2 text-xs text-red-600 dark:text-red-400">
              {JSON.stringify(status.error_details, null, 2)}
            </div>
          )}
        </div>
      )}

      {/* Completion Details */}
      {status.status === 'completed' && status.parsed_data && (
        <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-lg">
          <div className="flex items-center mb-2">
            <div className="text-green-500 mr-2">✅</div>
            <div className="text-sm font-medium text-green-700 dark:text-green-300">
              Processing Completed Successfully
            </div>
          </div>
          <div className="text-xs text-green-600 dark:text-green-400">
            Completed at: {new Date(status.completed_at!).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
};
